import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { TradeBenefitsYourFirm } from '../trade/TradeBenefitsYourFirm/TradeBenefitsYourFirm.tsx';

import { useServerVars } from '../../shared/ServerVarsContext.tsx';
import { UNASSIGNED_REP_EMAIL } from '../tradeBenefitsConstants.ts';
import { trackTradeBenefitsEvent } from '../tradeBenefitsTracking.ts';

import { type TradeBenefitsFaqSidebar_viewer$key } from './__generated__/TradeBenefitsFaqSidebar_viewer.graphql.ts';

import styles from './TradeBenefitsFaq.scss';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment TradeBenefitsFaqSidebar_viewer on Viewer {
        ...TradeBenefitsYourFirm_viewer
    }
`;

const TradeBenefitsFaqSidebar: FC<{
    isTrade: boolean;
    isFirmless?: boolean;
    viewer: TradeBenefitsFaqSidebar_viewer$key | null | undefined;
}> = ({ isTrade, isFirmless = false, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const { isMobile } = useServerVars();
    const showFirmInfo = isTrade && !isFirmless;

    if (showFirmInfo && isMobile) {
        return null;
    }

    if (showFirmInfo) {
        return <TradeBenefitsYourFirm viewer={viewer} />;
    }

    return (
        <div
            className={classnames(
                styles.sidebar,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.gapSmall,
                dibsCss.roundedXsmall,
                dibsCss.pMedium
            )}
        >
            <HeadingLevel>
                {Heading => (
                    <Heading
                        className={classnames(
                            styles.sidebarTitle,
                            dibsCss.sassyFontHeaderLarge,
                            dibsCss.m0
                        )}
                    >
                        <FormattedMessage
                            id="abt.TradeBenefitsFaqSidebar.title"
                            defaultMessage="Have questions?"
                        />
                    </Heading>
                )}
            </HeadingLevel>
            <div className={dibsCss.sassyFontBody}>
                <FormattedMessage
                    id="abt.TradeBenefitsFaqSidebar.content"
                    defaultMessage="Our Trade Customer Support team will gladly answer any questions you may have."
                />
            </div>
            <Link
                href={`mailto:${UNASSIGNED_REP_EMAIL}`}
                target="_blank"
                onClick={() =>
                    trackTradeBenefitsEvent({
                        stepInteractionName: 'support email clicked',
                        trigger: 'contact sidebar',
                    })
                }
            >
                <span className={dibsCss.sassyFontBodyTypeHeavy}>trade@1stdibs.com</span>
            </Link>
        </div>
    );
};

export { TradeBenefitsFaqSidebar };
